import { StandardListPage } from 'components/StandardListPage';
import useRestApi from 'hooks/useRestApi';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { apiUrl } from 'utils/apiUrl';
import { PaginatedTable } from 'components/PaginatedTable';
import {
  OnSortChangedParams,
  SortDirection,
} from '@bb-ui/react-library/dist/components/SortableTable/SortableTable.types';
import {
  SortableTableHeaderCell,
  makeStyles,
  Tooltip,
  TableHead,
  TableRow,
  TableCell,
} from '@bb-ui/react-library';
import { compareLearnVersions } from 'utils/learnVersion';
import { Item, ResponseData } from './ReleaseStages.types';
import { AddReleaseStageButton } from './AddReleaseStageButton';
import { DeleteReleaseStageButton } from './DeleteReleaseStageButton';
import { ReleaseStageMembersButton } from './ReleaseStageMembersButton';
import { ViewReleaseStageButton } from './ViewReleaseStageButton';

/**
 * The sort announcement names the column the reader just sorted, so the ids the
 * table sorts by are mapped to the labels the header shows.
 */
const SORT_COLUMN_LABEL_KEYS: Record<string, string> = {
  releaseStageName: 'releaseStages.releaseStageName',
  releaseStageLastReleaseVersion: 'releaseStages.lastReleaseVersion',
  releaseStageTargetReleaseVersion: 'releaseStages.targetReleaseVersion',
  releaseStageLearnInstances: 'releaseStages.releaseStageLearnInstance',
};

const useStyles = makeStyles(() => ({
  tooltip: {
    margin: '0',
  },
  button: {
    minWidth: 'auto',
    height: '1.5rem',
    padding: '0.2rem 0.5rem',
    fontSize: '0.75rem',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '0.3rem',
    justifyContent: 'center',
  },
}));

export const ReleaseStages: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const classes = useStyles();

  const {
    data: releaseStageData,
    error: releaseStageError,
    loading: releaseStageLoading,
    fetch: releaseStageFetching,
  } = useRestApi<ResponseData>(apiUrl('lct', 'releaseStage/'));

  const handleReload = () => {
    releaseStageFetching();
  };

  const releases = React.useMemo<Item[]>(() => releaseStageData?.items ?? [], [releaseStageData]);

  const search =
    (filter: string) =>
    ({ releaseStage }: Item) =>
      releaseStage.toLowerCase().includes(filter.toLowerCase());

  const [sortParams, setSortParams] = React.useState<Partial<OnSortChangedParams>>({
    sortColumnId: 'releaseStageName',
    sortDirection: 'asc',
  });

  // Sorting
  const sortedReleases = React.useMemo(() => {
    const { sortColumnId, sortDirection } = sortParams;
    const orderSign = sortDirection === 'asc' ? 1 : -1;

    const getComparator = (sortColumnId: string | undefined) => {
      switch (sortColumnId) {
        case 'releaseStageName':
          return (l1: { releaseStage: string }, l2: { releaseStage: string }) =>
            orderSign * l1.releaseStage.localeCompare(l2.releaseStage);
        case 'releaseStageLastReleaseVersion':
          return (l1: Item, l2: Item) => {
            if (!l1.lastReleaseVersion && !l2.lastReleaseVersion) return 0;
            if (!l1.lastReleaseVersion) return 1;
            if (!l2.lastReleaseVersion) return -1;
            return orderSign * compareLearnVersions(l1.lastReleaseVersion, l2.lastReleaseVersion);
          };
        case 'releaseStageTargetReleaseVersion':
          return (l1: { learnVersion: string }, l2: { learnVersion: string }) =>
            orderSign * compareLearnVersions(l1.learnVersion, l2.learnVersion);
        case 'releaseStageLearnInstances':
          return (l1: { tenantsCount: number }, l2: { tenantsCount: number }) =>
            orderSign * (l1.tenantsCount - l2.tenantsCount);
        default:
          return null;
      }
    };

    const comparator = getComparator(sortColumnId);

    if (comparator) {
      return [...releases].sort(comparator);
    }

    return releases;
  }, [releases, sortParams]);

  const getAriaSortMessage = (columnId?: string, sortDirection?: SortDirection) => {
    const columnLabel = t(SORT_COLUMN_LABEL_KEYS[columnId ?? ''] ?? '');
    const orderLabel =
      sortDirection === 'asc'
        ? t('global.paginatedTable.ascending')
        : t('global.paginatedTable.descending');
    return t('global.paginatedTable.sortedAriaMessage', { columnLabel, orderLabel });
  };

  return (
    <StandardListPage
      title={t('releaseStages.pageTitle')}
      headerControl={<AddReleaseStageButton onReload={handleReload} />}
      data-testid="release-stage-page"
      loading={releaseStageLoading && !releaseStageData}
      error={releaseStageError}
      errorTitle={t('releaseStages.loadError')}
      loadingTestId="fnds-release-stage-init"
      errorTestId="fnds-release-stage-no-data"
    >
      <PaginatedTable
        onSortChanged={(sortParams) => {
          setSortParams(sortParams);
          return true;
        }}
        getSortChangedAriaMessage={getAriaSortMessage}
        searchBoxProps={{ label: t('releaseStages.searchLabel') }}
        sortedData={sortedReleases}
        search={search}
        noMatchesMessage={(searchExpression) => t('releaseStages.noMatch', { searchExpression })}
        renderHead={() => (
          <TableHead>
            <TableRow>
              <SortableTableHeaderCell
                id="release-stage-table-header-release-name"
                columnId="releaseStageName"
                tooltip={t('releaseStages.sortByReleaseStageName')}
              >
                {t('releaseStages.releaseStageName')}
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                id="release-stage-table-header-last-release-version"
                columnId="releaseStageLastReleaseVersion"
                tooltip={t('releaseStages.sortByLastReleaseVersion')}
                tableCellProps={{ align: 'center' }}
              >
                {t('releaseStages.lastReleaseVersion')}
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                id="release-stage-table-header-target-release-version"
                columnId="releaseStageTargetReleaseVersion"
                tooltip={t('releaseStages.sortByTargetReleaseVersion')}
                tableCellProps={{ align: 'center' }}
              >
                {t('releaseStages.targetReleaseVersion')}
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                id="release-stage-table-header-release-learn-instances"
                columnId="releaseStageLearnInstances"
                tooltip={t('releaseStages.sortByReleaseStageLearnInstances')}
                tableCellProps={{ align: 'center' }}
              >
                {t('releaseStages.releaseStageLearnInstance')}
              </SortableTableHeaderCell>
              <Tooltip
                placement="top"
                title={t('releaseStages.toolTipForActions') || ''}
                arrow={true}
                classes={{ tooltip: classes.tooltip }}
              >
                <TableCell id="release-stage-table-header-release-actions" align="center">
                  {t('releaseStages.releaseStageActions')}
                </TableCell>
              </Tooltip>
            </TableRow>
          </TableHead>
        )}
        renderRow={(release, index) => (
          <TableRow
            key={release.releaseStage}
            aria-rowindex={index + 1}
            data-testid={`release-stage-table-row-${release.releaseStage}`}
          >
            <TableCell aria-colindex={1} aria-describedby="release-stage-table-header-release-name">
              {release.releaseStage}
            </TableCell>
            <TableCell
              align="center"
              aria-colindex={2}
              aria-describedby="release-stage-table-header-last-release-version"
            >
              {release.lastReleaseVersion ?? '—'}
            </TableCell>
            <TableCell
              align="center"
              aria-colindex={3}
              aria-describedby="release-stage-table-header-target-release-version"
            >
              {release.learnVersion}
            </TableCell>
            <TableCell
              align="center"
              aria-colindex={4}
              aria-describedby="release-stage-table-header-release-learn-instances"
            >
              {release.tenantsCount}
            </TableCell>
            <TableCell
              align="center"
              aria-colindex={5}
              aria-describedby="release-stage-table-header-release-actions"
            >
              <div className={classes.buttonContainer}>
                <DeleteReleaseStageButton
                  releaseStageID={release.id}
                  releaseStageName={release.releaseStage}
                  onReload={handleReload}
                />
                <ReleaseStageMembersButton
                  releaseStageId={release.id}
                  releaseStageName={release.releaseStage}
                  targetReleaseVersion={release.learnVersion}
                  lastReleaseVersion={release.lastReleaseVersion}
                  onReload={handleReload}
                />
                <ViewReleaseStageButton releaseStageID={release.id} />
              </div>
            </TableCell>
          </TableRow>
        )}
      />
    </StandardListPage>
  );
};
